import styled from 'styled-components';
import { Label } from '../../../components/uielements';
import { key, palette } from 'styled-theme';

const desktopBreakpoint = key('breakpoints.desktop', '1024px');
const tabletBreakpoint = key('breakpoints.tablet', '820px');
const mobileBreakpoint = key('breakpoints.mobile', '480px');

const gap = {
  tiny: key('sizes.gap.tiny', '4px'),
  small: key('sizes.gap.small', '8px'),
  normal: key('sizes.gap.normal', '16px'),
  medium: key('sizes.gap.medium', '24px'),
  big: key('sizes.gap.big', '32px'),
};

const radius = {
  tiny: key('sizes.borderRadius.tiny', '4px'),
  small: key('sizes.borderRadius.small', '8px'),
};

const font = {
  small: key('sizes.font.small', '12px'),
  normal: key('sizes.font.normal', '14px'),
  medium: key('sizes.font.medium', '16px'),
};

export const Main = styled.main`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100vw;

  @media (max-width: ${desktopBreakpoint}) {
    grid-template-columns: 1.2fr 1fr;
  }

  /* Right-hand image isn't needed on tablet or mobile — single column, form only */
  @media (max-width: ${tabletBreakpoint}) {
    grid-template-columns: 1fr;
  }
`;

export const SectionLeft = styled.section`
  background-color: ${palette('background', 0, '#ECEAF8')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${gap.big};
  padding: ${gap.big} 120px;
  box-sizing: border-box;
  width: 100%;
  overflow-y: auto;

  @media (max-width: ${desktopBreakpoint}) {
    padding: ${gap.big} 64px;
  }

  @media (max-width: ${tabletBreakpoint}) {
    padding: ${gap.big} ${gap.normal};
  }

  @media (max-width: ${mobileBreakpoint}) {
    padding: ${gap.normal};
  }
`;

export const SectionRight = styled.section`
  background-image: url(${(props) => props.bg});
  transition: background-image 0.5s ease-in-out;
  background-size: cover;
  background-position: center;

  @media (max-width: ${tabletBreakpoint}) {
    display: none;
  }
`;

export const Heading = styled(Label).attrs({ size: 'header2', color: 'black', weight: 'bold' })`
  text-transform: uppercase;
`;

export const SubHeading = styled(Label).attrs({ size: 'normal', color: 'black' })``;

export const TabletIntro = styled.div`
  display: none;

  @media (max-width: ${tabletBreakpoint}) {
    display: block;
    margin-bottom: ${gap.small};
  }
`;

export const PageTitle = styled.h1`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 2rem;
  font-weight: 700;
  color: #1f1f1f;
  margin: 0 0 ${gap.tiny} 0;
`;

export const PageSubtitle = styled.p`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.medium};
  color: #6b7280;
`;

export const FormCard = styled.div`
  display: grid;
  gap: ${gap.medium};
  @media (min-width: calc(${mobileBreakpoint} + 1px)) and (max-width: ${tabletBreakpoint}) {
    background-color: ${palette('background', 3, '#f8f5fe')};
    border-radius: ${radius.small};
    padding: ${gap.big} ${gap.medium};
  }
`;

export const PromptList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${gap.normal};
  justify-content: center;

  @media (max-width: ${mobileBreakpoint}) {
    gap: ${gap.small};
  }
`;

export const PromptItem = styled.li`
  font-size: ${font.normal};
  color: white;
  background-color: #3173da;
  padding: 0.75rem 1.5rem;
  border-radius: 0.7rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  text-align: center;
  list-style: none;
  transition:
    background-color 0.3s ease,
    box-shadow 0.3s ease;
  white-space: nowrap;
  width: auto;
  &:focus {
    outline: 2px solid #4a90e2;
    outline-offset: 2px;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${gap.medium};
  margin-top: ${gap.normal};

  @media (max-width: ${mobileBreakpoint}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const OptionButton = styled.button`
  background-color: #41e4c9;
  color: white;
  padding: ${gap.small};
  cursor: pointer;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.normal};
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.18px;
  border: none;
`;

export const Spinner = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  border: 3px solid #629dfb;
  border-radius: 50%;
  border-top: 3px solid #000;
  animation: spin 1s linear infinite;
  margin-right: ${gap.small};
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const ErrorMessage = styled.div`
  color: #d32f2f;
  background-color: #ffebee;
  padding: ${gap.small};
  margin: ${gap.small} 0;
  border-radius: ${radius.tiny};
`;
